import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { AuthContext } from '../../../../contexts/auth-context';
import { Tooltip as UiTooltip } from '../../../Tooltip';
import { formatAdserverType } from '../../../../utils';
import styles from './drillDownDrawer.module.css';
import { OI } from "../../../../onam/mark";
import { FEATURES } from "../../../../onam/features";

export interface MonthOption {
  label: string;
  startDate: string;
  endDate: string;
}

export type DrillEndpoint = 'event' | 'federation' | 'organization' | 'adserver';

export interface DrillDownTarget {
  cardTitle: string;
  startDate?: string;
  endDate?: string;
  availableMonths?: MonthOption[];
  defaultMonthStartDate?: string;
  eventType?: string | null;
  endpoint?: DrillEndpoint;
  entityParams?: Record<string, string | number>;
  /**
   * When true (e.g. ManagementDashboardDetails month drill), lines and table rows
   * are split by event_type while still using the day-level API for `endpoint`.
   */
  chartLinesByEventType?: boolean;
  /** Which numeric field to plot on the daily line chart when `chartLinesByEventType` is set. */
  dayLevelChartMetric?: 'total_count' | 'query_count';
  /**
   * When set, only dimensions whose key appears in this list are shown in the chart and table.
   * Used to carry forward entity / event-type selections from the parent chart card.
   */
  allowedDimensionKeys?: string[];
}

interface DayRecord {
  date: string;
  total_count: number;
  query_count: number;
  row_count?: number;
  event_type?: string;
  federation_id?: string;
  federation_name?: string;
  organization_id?: string;
  organization_name?: string;
  destination_adserver_type?: string;
  [key: string]: any;
}

interface DrillDownDrawerProps {
  target: DrillDownTarget | null;
  onClose: () => void;
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  query: '#0D6AB3',
  marked_engaged: '#2ACDFF',
  audience_transferred: '#F59E0B',
};

const ENTITY_COLORS = [
  '#2563EB', '#10B981', '#EF4444', '#8B5CF6', '#F97316',
  '#06B6D4', '#84CC16', '#EC4899', '#14B8A6', '#F59E0B',
];

const formatCount = (n: number) => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

const formatCountRowData = (val: string | number) => {
  return Number(val).toLocaleString("en-US");
};

const formatEventType = (et: string) =>
  et.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace("Marked Engaged", "Marked/Engaged");

const formatDay = (dateStr: string) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Extract a stable key and display label from a record based on the active endpoint
function nonEmpty(value: string | undefined | null): string | undefined {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed || undefined;
}

function getDimensionKeyAndLabel(r: DayRecord, endpoint: DrillEndpoint): { key: string; label: string } {
  switch (endpoint) {
    case 'federation': {
      const key = nonEmpty(r.federation_id) ?? nonEmpty(r.federation_name) ?? 'Unknown';
      const label = nonEmpty(r.federation_name) ?? nonEmpty(r.federation_id) ?? 'Unknown';
      return { key, label };
    }
    case 'organization': {
      const key = nonEmpty(r.organization_id) ?? nonEmpty(r.organization_name) ?? 'Unknown';
      const label = nonEmpty(r.organization_name) ?? nonEmpty(r.organization_id) ?? 'Unknown';
      return { key, label };
    }
    case 'adserver': {
      const key = r.destination_adserver_type ?? 'Unknown';
      const label = formatAdserverType(r.destination_adserver_type ?? 'Unknown');
      return { key, label };
    }
    default: {
      const key = r.event_type ?? 'unknown';
      const label = formatEventType(r.event_type ?? 'unknown');
      return { key, label };
    }
  }
}

function getDimensionColor(key: string, index: number, endpoint: DrillEndpoint): string {
  if (endpoint === 'event') {
    return EVENT_TYPE_COLORS[key] ?? ENTITY_COLORS[index % ENTITY_COLORS.length];
  }
  return ENTITY_COLORS[index % ENTITY_COLORS.length];
}

const TABLE_HEADER: Record<DrillEndpoint, string> = {
  event: 'Event Type',
  federation: 'Federation',
  organization: 'Organization',
  adserver: 'Ad Server Type',
};

const DrillDownDrawer: React.FC<DrillDownDrawerProps> = ({ target, onClose }) => {
  const {
    getEventDayLevelCount,
    getFederationDayLevelCount,
    getOrganizationDayLevelCount,
    getAdserverDayLevelCount,
    tableGetEventDayLevelCount,
    tableGetFederationDayLevelCount,
    tableGetOrganizationDayLevelCount,
    tableGetAdserverDayLevelCount,
  } = useContext(AuthContext);

  const [selectedMonth, setSelectedMonth] = useState<MonthOption | null>(null);
  const [chartRecords, setChartRecords]   = useState<DayRecord[]>([]);
  const [tableRecords, setTableRecords]   = useState<DayRecord[]>([]);
  const [chartLoading, setChartLoading]   = useState(false);
  const [tableLoading, setTableLoading]   = useState(false);
  const [chartError, setChartError]       = useState(false);
  const [tableError, setTableError]       = useState(false);

  const loading    = chartLoading || tableLoading;
  const fetchError = chartError || tableError;

  const isOpen = target !== null;
  const endpoint: DrillEndpoint = target?.endpoint ?? 'event';
  const dimensionEndpoint: DrillEndpoint =
    target?.chartLinesByEventType ? 'event' : endpoint;
  const lineChartMetric: 'total_count' | 'query_count' =
    target?.chartLinesByEventType && target?.dayLevelChartMetric === 'query_count'
      ? 'query_count'
      : 'total_count';

  // Default-select the appropriate month whenever target changes
  useEffect(() => {
    if (!target) return;
    if (target.availableMonths?.length) {
      const toSelect = target.defaultMonthStartDate
        ? (target.availableMonths.find(m => m.startDate === target.defaultMonthStartDate)
            ?? target.availableMonths[target.availableMonths.length - 1])
        : target.availableMonths[target.availableMonths.length - 1];
      setSelectedMonth(toSelect);
    } else {
      setSelectedMonth(null);
    }
  }, [target]);

  const effectiveStart = selectedMonth?.startDate ?? target?.startDate ?? null;
  const effectiveEnd   = selectedMonth?.endDate   ?? target?.endDate   ?? null;

  // Original APIs — chart data only
  const chartApiFn = (() => {
    switch (target?.endpoint) {
      case 'federation':   return getFederationDayLevelCount;
      case 'organization': return getOrganizationDayLevelCount;
      case 'adserver':     return getAdserverDayLevelCount;
      default:             return getEventDayLevelCount;
    }
  })();

  // Table-specific APIs — table data only
  const tableApiFn = (() => {
    switch (target?.endpoint) {
      case 'federation':   return tableGetFederationDayLevelCount;
      case 'organization': return tableGetOrganizationDayLevelCount;
      case 'adserver':     return tableGetAdserverDayLevelCount;
      default:             return tableGetEventDayLevelCount;
    }
  })();

  // Fetch chart data from original APIs
  useEffect(() => {
    if (!effectiveStart || !effectiveEnd) return;

    const controller = new AbortController();
    setChartLoading(true);
    setChartError(false);
    setChartRecords([]);

    const args: any = {
      start_date: effectiveStart,
      end_date: effectiveEnd,
      ...target?.entityParams,
    };
    if (target?.eventType) args.event_type = target.eventType;

    chartApiFn({ args, signal: controller.signal })
      .then((res: any) => {
        if (!controller.signal.aborted) {
          setChartRecords(res?.data ?? []);
          setChartLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setChartError(true);
          setChartLoading(false);
        }
      });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveStart, effectiveEnd, target?.eventType, target?.endpoint, JSON.stringify(target?.entityParams)]);

  // Fetch table data from dedicated table APIs
  useEffect(() => {
    if (!effectiveStart || !effectiveEnd) return;

    const controller = new AbortController();
    setTableLoading(true);
    setTableError(false);
    setTableRecords([]);

    const args: any = {
      start_date: effectiveStart,
      end_date: effectiveEnd,
      ...target?.entityParams,
    };
    if (target?.eventType) args.event_type = target.eventType;

    tableApiFn({ args, signal: controller.signal })
      .then((res: any) => {
        if (!controller.signal.aborted) {
          setTableRecords(res?.data ?? []);
          setTableLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setTableError(true);
          setTableLoading(false);
        }
      });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveStart, effectiveEnd, target?.eventType, target?.endpoint, JSON.stringify(target?.entityParams)]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Build dimension lists separately for chart (original API) and table (table* API)
  const buildDimensions = (rows: DayRecord[]) => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      const { key, label } = getDimensionKeyAndLabel(r, dimensionEndpoint);
      if (!seen.has(key)) seen.set(key, label);
    }
    return Array.from(seen.entries()).map(([key, label]) => ({ key, label }));
  };

  const allChartDimensions = buildDimensions(chartRecords);
  const dimensions = target?.allowedDimensionKeys?.length
    ? allChartDimensions.filter(d => target.allowedDimensionKeys!.includes(d.key))
    : allChartDimensions;

  const allTableDimensions = buildDimensions(tableRecords);
  const tableDimensions = target?.allowedDimensionKeys?.length
    ? allTableDimensions.filter(d => target.allowedDimensionKeys!.includes(d.key))
    : allTableDimensions;

  // Pivot chart records into per-day data points for the line chart
  const lineChartData = (() => {
    const map: Record<string, Record<string, any>> = {};
    for (const r of chartRecords) {
      const dayLabel = formatDay(r.date);
      if (!map[dayLabel]) map[dayLabel] = { day: dayLabel };
      const { key } = getDimensionKeyAndLabel(r, dimensionEndpoint);
      const add =
        lineChartMetric === 'query_count'
          ? (r.query_count ?? 0)
          : (r.total_count ?? 0);
      map[dayLabel][`${key}_total`] = (map[dayLabel][`${key}_total`] ?? 0) + add;
    }
    return Object.values(map);
  })();

  const activeMonthLabel = selectedMonth?.label
    ?? (target?.startDate
      ? new Date(target.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : '');

  const hasMonthPicker = !!(target?.availableMonths?.length);
  const tableHeader = TABLE_HEADER[dimensionEndpoint];

  const overlayRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div
        ref={overlayRef}
        className={`${styles.backdrop} ${isOpen ? styles.backdropVisible : ''}`}
        onClick={onClose}
      />

      <div className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ''}`}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <span className={styles.headerTitle}>{target?.cardTitle ?? ''}</span>
            <span className={styles.headerSub}>Daily breakdown — {activeMonthLabel}</span>
          </div>
          <UiTooltip text="Close" placement="bottom">
          <button className={styles.closeBtn} onClick={onClose}>
            {FEATURES.curationSkin ? <OI n="close" size={16} /> : (
<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="#475569" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
)}
          </button>
          </UiTooltip>
        </div>

        <div className={styles.divider} />

        {/* Month selector strip */}
        {hasMonthPicker && (
          <div className={styles.monthStrip}>
            {target!.availableMonths!.map(m => (
              <button
                key={m.startDate}
                className={`${styles.monthPill} ${selectedMonth?.startDate === m.startDate ? styles.monthPillActive : ''}`}
                onClick={() => setSelectedMonth(m)}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        <div className={styles.body}>
          {loading ? (
            <div className={styles.shimmerArea}>
              <div className={`${styles.shimmerBlock} ${styles.shimmerChart}`} />
              <div className={styles.shimmerLegendRow}>
                {[1, 2, 3].map(i => <div key={i} className={`${styles.shimmerBlock} ${styles.shimmerPill}`} />)}
              </div>
            </div>
          ) : fetchError ? (
            <div className={styles.errorState}>Failed to load data. Please try again.</div>
          ) : !lineChartData.length ? (
            <div className={styles.emptyState}>No data available for this month.</div>
          ) : (
            <>
              {/* Chart stays pinned — never scrolls */}
              <div className={styles.chartArea}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineChartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke="#D4D4D8" />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 10, fill: '#71717a' }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#71717a' }}
                      axisLine={false}
                      tickLine={false}
                      width={40}
                      tickFormatter={formatCount}
                    />
                    <Tooltip
                      wrapperStyle={{ zIndex: 1000 }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const nonZero = payload
                          .filter((p: any) => p.value > 0)
                          .sort((a: any, b: any) => b.value - a.value);
                        return (
                          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 10, minWidth: 160, pointerEvents: 'none' }}>
                            <div style={{ padding: '6px 10px 5px', fontWeight: 600, color: '#092538', borderBottom: '1px solid #f1f5f9' }}>{label}</div>
                            <div style={{ padding: '4px 0' }}>
                              {nonZero.map((entry: any, i: number) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px' }}>
                                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color, flexShrink: 0 }} />
                                  <span style={{ flex: 1, color: '#475569' }}>{entry.name}</span>
                                  <span style={{ color: '#092538', fontWeight: 600, marginLeft: 8 }}>{formatCount(entry.value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }}
                    />
                    {dimensions.map((dim, i) => (
                      <Line
                        key={dim.key}
                        type="monotone"
                        dataKey={`${dim.key}_total`}
                        name={dim.label}
                        stroke={getDimensionColor(dim.key, i, dimensionEndpoint)}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Scrollable table — grows within remaining height, chart stays visible */}
              <div className={styles.tableScrollArea}>
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>{tableHeader}</th>
                        <th className={styles.th}>Audience Volume</th>
                        <th className={styles.th}>Queries Executed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableDimensions.map((dim, i) => {
                        const dimRecords = tableRecords.filter(
                          r => getDimensionKeyAndLabel(r, dimensionEndpoint).key === dim.key,
                        );
                        const total = dimRecords.reduce((s, r) => s + r.total_count, 0);
                        const query = dimRecords.reduce((s, r) => s + r.query_count, 0);
                        return (
                          <tr key={dim.key} className={styles.tr}>
                            <td className={styles.td}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', backgroundColor: getDimensionColor(dim.key, i, dimensionEndpoint), flexShrink: 0 }} />
                                {dim.label}
                              </span>
                            </td>
                            <td className={styles.td}>{formatCountRowData(total)}</td>
                            <td className={styles.td}>{formatCountRowData(query)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DrillDownDrawer;
